import {
  Box, Typography, Card, CardContent, Grid2 as Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Divider, Alert, Skeleton, Select, MenuItem, FormControl, InputLabel
} from "@mui/material"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import {
  fetchSpecialistWallet,
  fetchSpecialistTransactions,
} from "../../../../redux/admin-slices/wallet.slice"
import PayoutDialog from "../../../../components/Dialog/PayoutDialog"
import PauseResumeDialog from "../../../../components/Dialog/PauseResumeDialog"
import FlagUnflagDialog from "../../../../components/Dialog/FlagUnflagDialog"
import TablePagination from "../../../../components/common/PaginationSkell"
import { formatDate } from "../../../../utils/helper"

const fmt = (n, cur = "inr") => {
  const isUsd = cur?.toLowerCase() === "usd";
  const symbol = isUsd ? "$" : "₹";
  const locale = isUsd ? "en-US" : "en-IN";
  return `${symbol}${(n / 100).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

// ─── Balance Card ─────────────────────────────────────────────────────────────
function BalanceCard({ label, value, color = "inherit", sub }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", height: "100%" }}>
      <CardContent>
        <Typography fontSize={11} fontWeight={700} color="text.secondary"
          textTransform="uppercase" letterSpacing={0.8}>{label}</Typography>
        <Typography fontSize={26} fontWeight={800} color={color} mt={0.5}>{value}</Typography>
        {sub && <Typography fontSize={12} color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  )
}

// ─── Transaction type → display config ───────────────────────────────────────
const TX_META = {
  credit_pending:        { label: "Payment Received",      color: "#1565c0", bg: "#e3f2fd" },
  release_to_available:  { label: "Released to Available", color: "#2e7d32", bg: "#e8f5e9" },
  payout:                { label: "Payout to Specialist",  color: "#6a1b9a", bg: "#f3e5f5" },
  reversal:              { label: "Refund Reversal",       color: "#e65100", bg: "#fff3e0" },
  post_payout_reversal:  { label: "⚠ Post-Payout Reversal", color: "#b71c1c", bg: "#ffebee" },
}

function TxTypeChip({ type }) {
  const meta = TX_META[type] || { label: type, color: "#555", bg: "#f5f5f5" }
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        backgroundColor: meta.bg,
        color: meta.color,
        fontWeight: 600,
        fontSize: 11,
        border: `1px solid ${meta.color}33`,
      }}
    />
  )
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function SpecialistWallet() {
  const dispatch      = useDispatch()
  const { userId }    = useParams()

  const [wallet,    setWallet]    = useState(null)
  const [txData,    setTxData]    = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [txLoading, setTxLoading] = useState(false)
  const [txType,    setTxType]    = useState("")

  // Dialog state
  const [payoutOpen,  setPayoutOpen]  = useState(false)
  const [pauseState,  setPauseState]  = useState(null) // { mode }
  const [flagState,   setFlagState]   = useState(null) // { mode }

  const loadWallet = async () => {
    setLoading(true)
    try {
      const res = await dispatch(fetchSpecialistWallet({ specialistId: userId })).unwrap()
      setWallet(res)
    } catch { /* toast */ }
    finally { setLoading(false) }
  }

  const loadTx = async (overrides = {}) => {
    setTxLoading(true)
    const selectedType = "type" in overrides ? overrides.type : txType
    try {
      const res = await dispatch(fetchSpecialistTransactions({
        specialistId: userId,
        type: selectedType || undefined,
        page: overrides.page ?? 1,
        limit: 15,
      })).unwrap()
      setTxData(res)
    } catch { /* toast */ }
    finally { setTxLoading(false) }
  }

  useEffect(() => {
    if (userId) { loadWallet(); loadTx() }
    // eslint-disable-next-line
  }, [userId])

  const afterAction = () => { loadWallet(); loadTx() }

  const w = wallet?.wallet
  const specialist = wallet
    ? { ...w, _id: userId, fullName: w?.specialistId?.fullName, email: w?.specialistId?.email,
        stripeOnboardingComplete: wallet?.stripeOnboardingComplete }
    : null

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={40} width={200} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid key={i} size={{ xs: 6, lg: 3 }}>
              <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
                <CardContent><Skeleton height={60} /></CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Skeleton height={300} sx={{ mt: 3 }} />
      </Box>
    )
  }

  if (!w) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          This specialist has not earned any payments yet. Their wallet will appear here once
          a booking is confirmed and paid.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Risk Banner */}
      {(w.flaggedForReview || w.payoutPaused) && (
        <Alert severity={w.flaggedForReview ? "error" : "warning"} sx={{ mb: 3 }}>
          {w.flaggedForReview && <><strong>🚩 Account Flagged:</strong> {w.flagReason}<br /></>}
          {w.payoutPaused && <><strong>⏸ Payouts Paused:</strong> {w.payoutPausedReason}</>}
        </Alert>
      )}

      {/* Balance cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, lg: 3 }}>
          <BalanceCard label="Pending" value={fmt(w.pendingBalance, w.currency)} color="#1565c0"
            sub="Released after booking completes" />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <BalanceCard label="Available" value={fmt(w.availableBalance, w.currency)} color="#2e7d32"
            sub="Ready to pay out" />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <BalanceCard label="Total Earned" value={fmt(w.totalEarned, w.currency)} color="#5c35a3" />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <BalanceCard label="Total Paid Out" value={fmt(w.totalPaidOut, w.currency)} color="#555" />
        </Grid>
      </Grid>

      {/* Risk status + Action buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 3 }}>
        {w.flaggedForReview
          ? <Chip label="🚩 Flagged" color="error" size="small" sx={{ fontWeight: 600 }} />
          : <Chip label="✓ Not Flagged" color="success" size="small" variant="outlined" />}
        {w.payoutPaused
          ? <Chip label="⏸ Paused" color="warning" size="small" sx={{ fontWeight: 600 }} />
          : <Chip label="▶ Payouts Active" color="success" size="small" variant="outlined" />}

        <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small" variant="contained" color="primary"
            disabled={w.payoutPaused || w.flaggedForReview || w.availableBalance === 0}
            onClick={() => setPayoutOpen(true)}
            sx={{ textTransform: "none" }}
          >
            💳 Pay Out
          </Button>
          {w.payoutPaused
            ? <Button size="small" variant="outlined" color="success"
                onClick={() => setPauseState({ mode: "resume" })}
                sx={{ textTransform: "none" }}>▶ Resume</Button>
            : <Button size="small" variant="outlined" color="warning"
                onClick={() => setPauseState({ mode: "pause" })}
                sx={{ textTransform: "none" }}>⏸ Pause</Button>
          }
          {w.flaggedForReview
            ? <Button size="small" variant="outlined" color="success"
                onClick={() => setFlagState({ mode: "unflag" })}
                sx={{ textTransform: "none" }}>✓ Unflag</Button>
            : <Button size="small" variant="outlined" color="error"
                onClick={() => setFlagState({ mode: "flag" })}
                sx={{ textTransform: "none" }}>🚩 Flag</Button>
          }
        </Box>
      </Box>

      {/* Ledger */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", mb: 3 }}>
        <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography fontWeight={700} fontSize={15}>Wallet Ledger</Typography>
          <FormControl size="small" sx={{ minWidth: 200, ml: "auto" }}>
            <InputLabel>Filter by type</InputLabel>
            <Select
              label="Filter by type"
              value={txType}
              onChange={(e) => { setTxType(e.target.value); loadTx({ type: e.target.value }) }}
            >
              <MenuItem value="">All transactions</MenuItem>
              <MenuItem value="credit_pending">Payment Received</MenuItem>
              <MenuItem value="release_to_available">Released to Available</MenuItem>
              <MenuItem value="payout">Payout</MenuItem>
              <MenuItem value="reversal">Refund Reversal</MenuItem>
              <MenuItem value="post_payout_reversal">Post-Payout Reversal</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "th": { fontWeight: 700, color: "#555", fontSize: 12 } }}>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Before</TableCell>
                <TableCell>After</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                    </TableRow>
                  ))
                : txData?.transactions?.map((tx, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <TxTypeChip type={tx.type} />
                        {tx.type === "post_payout_reversal" && (
                          <Chip size="small" label="⚠ Manual Recovery Required"
                            color="error" sx={{ ml: 1, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700} fontSize={13}
                          color={["payout","reversal","post_payout_reversal"].includes(tx.type) ? "error.main" : "success.main"}>
                          {["payout","reversal","post_payout_reversal"].includes(tx.type) ? "−" : "+"}
                          {fmt(Math.abs(tx.amount), tx.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {tx.balanceBefore ? (
                          <Box>
                            <Typography fontSize={12} color="text.secondary">
                              Avail: {fmt(tx.balanceBefore.available, tx.currency)}
                            </Typography>
                            <Typography fontSize={10} color="text.disabled">
                              Pend: {fmt(tx.balanceBefore.pending, tx.currency)}
                            </Typography>
                          </Box>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        {tx.balanceAfter ? (
                          <Box>
                            <Typography fontSize={12} fontWeight={600} color="text.primary">
                              Avail: {fmt(tx.balanceAfter.available, tx.currency)}
                            </Typography>
                            <Typography fontSize={10} color="text.disabled">
                              Pend: {fmt(tx.balanceAfter.pending, tx.currency)}
                            </Typography>
                          </Box>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tx.note || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12}>{formatDate(tx.createdAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
              }
              {!txData?.transactions?.length && !txLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No transactions yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 2 }}>
          <TablePagination
            pagination={txData?.pagination}
            length={txData?.transactions?.length}
            loading={txLoading}
            cb={(f) => loadTx({ page: f.currentPage })}
          />
        </Box>
      </Card>

      {/* Dialogs */}
      <PayoutDialog
        open={payoutOpen}
        specialist={specialist}
        onClose={() => setPayoutOpen(false)}
        onSuccess={afterAction}
      />
      <PauseResumeDialog
        open={Boolean(pauseState)}
        mode={pauseState?.mode}
        specialist={specialist}
        onClose={() => setPauseState(null)}
        onSuccess={afterAction}
      />
      <FlagUnflagDialog
        open={Boolean(flagState)}
        mode={flagState?.mode}
        specialist={specialist}
        onClose={() => setFlagState(null)}
        onSuccess={afterAction}
      />
    </Box>
  )
}
